import Card from "@mui/material/Card";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";

import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import LoadingButton from "@mui/lab/LoadingButton";
import { APIRequestErrorInterface } from "../../api/fetchApi";
import LoginInput from "../../components/inputs/LoginInput";
import { MinPasswordLength } from "../../constants/main";
import { FetchStatusEnum, FetchStatusStrings } from "../../enums/fetchStatusEnum";
import i18nStore from "../../i18n/store";
import { validateEmailFormat } from "../../utils/validateEmail";
import { SignUpRequestSchema } from "../../api/schemas/requests/auth";
import { API } from "../../api/api";
import PasswordInput from "../../components/inputs/PasswordInput";
import EmailInput from "../../components/inputs/EmailInput";
import ErrorMsg from "../../ui/ErrorMsg";
import { loginPath, successRegistrationPath } from "../../routes/paths";
import TextField from "@mui/material/TextField";


const AuthSignUpForm = observer(() => {
    const translations = i18nStore.getTranslations;
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>("");
    const [name, setName] = useState<string>("");

    const [password, setPassword] = useState<string | undefined>();
    const [password2, setPassword2] = useState<string | undefined>();
    const [isPasswordsMatch, setIsPasswordsMatch] = useState<boolean | undefined>();
    const [isPasswordHasGoodLen, setIsPasswordHasGoodLen] = useState<boolean | undefined>();
    const [isPasswordsOK, setIsPasswordsOK] = useState<boolean | undefined>();

    const [isFormDisabled, setIsFormDisabled] = useState(true);

    const [errorText, setErrorText] = useState<string>("");
    const [actionStatus, setActionStatus] = useState<FetchStatusStrings>(FetchStatusEnum.IDLE);

    useEffect(() => {
        setActionStatus(FetchStatusEnum.IDLE);
    }, [password, password2, email, name]);

    useEffect(() => {
        // Если пароли еще не изменялись
        if (password === undefined && password2 === undefined) return;

        // Если пароль короткий
        if (password && password.length < MinPasswordLength) {
            setIsPasswordHasGoodLen(false);
            return;
        }
        setIsPasswordHasGoodLen(true);

        // пользователь еще не начал вводить второй пароль
        if (password2 === undefined) return;

        // Если пароли не совпадают
        if (password !== password2) {
            setIsPasswordsMatch(false);
            return;
        }
        setIsPasswordsMatch(true);
    }, [password, password2]);

    useEffect(() => {
        if (isPasswordHasGoodLen === undefined || isPasswordsMatch === undefined) {
            setIsPasswordsOK(undefined);
        }
        setIsPasswordsOK(isPasswordHasGoodLen && isPasswordsMatch);
    }, [isPasswordsMatch, isPasswordHasGoodLen]);

    useEffect(() => {
        setIsFormDisabled(!isPasswordsOK || !validateEmailFormat(email) || !name);
    }, [isPasswordsOK, email, name]);

    function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (isFormDisabled) return;
        if (!password) return;

        setActionStatus(FetchStatusEnum.FETCHING);

        const signUpRequestData: SignUpRequestSchema = {
            email: email,
            name: name,
            password: password,
        };
        API.auth
            .signUp(signUpRequestData)
            .then(() => {
                setPassword("");
                setActionStatus(FetchStatusEnum.SUCCESS);
                navigate(successRegistrationPath, { state: { email: email } });
            })
            .catch((err: APIRequestErrorInterface) => {
                setActionStatus(FetchStatusEnum.ERROR);

                if (err.status >= 500) {
                    setErrorText(err.details.detail || translations.errServiceUnavailableMsg);
                } else if (err.status >= 400) {
                    setErrorText(err.details.detail || "Something went wrong");
                }
            });
    }

    return (
        <Container component="main" maxWidth="xs" sx={{ p: { xs: 0, md: 2, lg: 3} }}>
            <CssBaseline />
            <Card
                sx={{
                    p: { xs: 1, md: 2, lg: 3},
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography component="h1" variant="h5">
                    {translations.signUp}
                </Typography>
                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
                    <Grid container>
                        <EmailInput email={email} setEmail={setEmail} />
                        <LoginInput
                            login={name}
                            setLogin={setName}
                            myLogin={false}
                            size="medium"
                        />

                        <Box sx={{ mt: 2, width: "100%" }}>
                            <PasswordInput password={password || ""} setPassword={setPassword} />
                        </Box>
                        <Box sx={{ my: 2, width: "100%" }}>
                            <PasswordInput
                                password={password2 || ""}
                                setPassword={setPassword2}
                                label={translations.repeatPassword}
                            />
                        </Box>

                        <ErrorMsg
                            errText={translations.errPasswordsDontMatchMsg}
                            visible={isPasswordsMatch !== undefined && !isPasswordsMatch}
                        />
                        <ErrorMsg
                            errText={translations.errPasswordsTooShortMsg}
                            visible={isPasswordHasGoodLen !== undefined && !isPasswordHasGoodLen}
                        />

                        <ErrorMsg errText={errorText} visible={actionStatus === FetchStatusEnum.ERROR} />
                    </Grid>

                    <LoadingButton
                        disabled={isFormDisabled}
                        loading={actionStatus === FetchStatusEnum.FETCHING}
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                    >
                        {translations.signUp}
                    </LoadingButton>

                    <Grid container justifyContent="flex-end">
                        <Grid>
                            <Link
                                href={loginPath}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(loginPath);
                                }}
                                variant="body1"
                                sx={{
                                    "&:hover": {
                                        cursor: "pointer",
                                    },
                                }}
                            >
                                {translations.alreadyHaveAccountSignIn}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Card>
        </Container>
    );
});


export default AuthSignUpForm;
